import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Globe, Bell, Key, Link, User, Shield, Palette, Volume2,
  Save, Check, ExternalLink, MessageCircle, Link2, Unlink, Send, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import Sidebar from "../components/Sidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "ar", name: "العربية (Arabic)" },
  { code: "es", name: "Español (Spanish)" },
  { code: "ur", name: "اردو (Urdu)" },
  { code: "zh", name: "中文 (Chinese)" },
  { code: "fr", name: "Français (French)" },
];

export default function Settings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    defaultLanguage: "en",
    voiceFeedback: true,
    autoPost: false,
    notifications: true,
    darkMode: true
  });
  const [apiKeys, setApiKeys] = useState({
    elevenlabs: "",
    tiktok: "",
    instagram: "",
    linkedin: "",
    twitter: ""
  });
  
  // Telegram state
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [telegramLoading, setTelegramLoading] = useState(true);
  const [linkCode, setLinkCode] = useState("");
  const [linking, setLinking] = useState(false);
  const [telegramSettings, setTelegramSettings] = useState({
    enabled: true,
    notify_before_post: true,
    auto_approve_after_hours: 24
  });

  useEffect(() => {
    fetchTelegramStatus();
  }, []);

  const fetchTelegramStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/telegram/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTelegramStatus(response.data);
      if (response.data.settings) {
        setTelegramSettings(response.data.settings);
      }
    } catch (error) {
      console.error("Failed to fetch Telegram status:", error);
    } finally {
      setTelegramLoading(false);
    }
  };

  const linkTelegram = async () => {
    if (!linkCode.trim()) {
      toast.error("Please enter the verification code");
      return;
    }
    setLinking(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/telegram/link`, 
        { code: linkCode.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Telegram account linked successfully!");
      setLinkCode("");
      fetchTelegramStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or expired code");
    } finally {
      setLinking(false);
    }
  };

  const unlinkTelegram = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/telegram/unlink`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Telegram account unlinked");
      fetchTelegramStatus();
    } catch (error) {
      toast.error("Failed to unlink Telegram");
    }
  };

  const saveTelegramSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/telegram/settings`, telegramSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Telegram settings saved!");
    } catch (error) {
      toast.error("Failed to save Telegram settings");
    }
  };

  const testTelegramNotification = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/telegram/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Test notification sent to your Telegram!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test notification");
    }
  };

  const handleSave = () => {
    // In a real app, this would save to backend
    localStorage.setItem('vcmo_settings', JSON.stringify(settings));
    localStorage.setItem('vcmo_api_keys', JSON.stringify(apiKeys));
    toast.success("Settings saved successfully!");
  };

  return (
    <div className="min-h-screen flex bg-background">
      <Sidebar active="settings" />
      
      <main className="flex-1 md:ml-64 p-6 lg:p-8 overflow-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Settings</h1>
            <p className="text-muted-foreground mt-1">Manage your preferences and integrations</p>
          </div>
          
          <Button onClick={handleSave} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="glass border-white/10">
            <TabsTrigger value="general" data-testid="general-tab">General</TabsTrigger>
            <TabsTrigger value="telegram" data-testid="telegram-tab">
              <MessageCircle className="w-4 h-4 mr-2" />
              Telegram
            </TabsTrigger>
            <TabsTrigger value="integrations" data-testid="integrations-tab">Integrations</TabsTrigger>
            <TabsTrigger value="voice" data-testid="voice-tab">Voice & AI</TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general">
            <div className="grid gap-6">
              <Card className="glass-card border-white/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" />
                    Language & Region
                  </CardTitle>
                  <CardDescription>Configure your default language preferences</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Default Content Language</Label>
                    <Select 
                      value={settings.defaultLanguage} 
                      onValueChange={(v) => setSettings({...settings, defaultLanguage: v})}
                    >
                      <SelectTrigger className="w-full bg-white/5 border-white/10" data-testid="default-language">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="glass border-white/10">
                        {SUPPORTED_LANGUAGES.map((lang) => (
                          <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      This will be the default language for generated content
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card border-white/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-primary" />
                    Notifications
                  </CardTitle>
                  <CardDescription>Manage your notification preferences</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Push Notifications</Label>
                      <p className="text-xs text-muted-foreground">Get notified when posts are published</p>
                    </div>
                    <Switch 
                      checked={settings.notifications}
                      onCheckedChange={(v) => setSettings({...settings, notifications: v})}
                      data-testid="notifications-switch"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card border-white/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    Campaign Settings
                  </CardTitle>
                  <CardDescription>Configure default campaign behavior</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Auto-Post Content</Label>
                      <p className="text-xs text-muted-foreground">Automatically post when scheduled time arrives</p>
                    </div>
                    <Switch 
                      checked={settings.autoPost}
                      onCheckedChange={(v) => setSettings({...settings, autoPost: v})}
                      data-testid="auto-post-switch"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Telegram Human-in-the-Loop */}
          <TabsContent value="telegram">
            <div className="grid gap-6">
              <Card className="glass-card border-white/5">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-500/20">
                        <MessageCircle className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Telegram Bot Connection</CardTitle>
                        <CardDescription>Human-in-the-Loop content approval</CardDescription>
                      </div>
                    </div>
                    {telegramLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    ) : telegramStatus?.bot_configured ? (
                      <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                        <Check className="w-3 h-3 mr-1" /> Bot Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30">
                        Not Configured
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {telegramLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : telegramStatus?.bot_configured ? (
                    <>
                      {/* Connection Status */}
                      <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium">
                              Bot: @{telegramStatus.bot_info?.username || "YASNOMarketingBot"}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {telegramStatus.user_connected 
                                ? `✅ Connected as @${telegramStatus.settings?.username || 'User'}` 
                                : "⚠️ Your account is not connected yet"}
                            </p>
                          </div>
                          {telegramStatus.user_connected && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={testTelegramNotification}
                                className="border-white/10"
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Test
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={unlinkTelegram}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                              >
                                <Unlink className="w-4 h-4 mr-2" />
                                Unlink
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Link Account Instructions */}
                      {!telegramStatus.user_connected && (
                        <div className="space-y-4">
                          <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                            <h4 className="font-medium text-blue-400 mb-2">How to Connect Your Telegram:</h4>
                            <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                              <li>Open Telegram app on your phone</li>
                              <li>Search for <span className="text-blue-400 font-mono">@{telegramStatus.bot_info?.username || "YASNOMarketingBot"}</span></li>
                              <li>Tap <span className="text-blue-400 font-bold">Start</span> or send <span className="text-blue-400 font-mono">/start</span></li>
                              <li>Copy the verification code the bot sends you</li>
                              <li>Paste it below and click "Link Account"</li>
                            </ol>
                          </div>
                          
                          <div className="flex gap-3">
                            <Input
                              placeholder="Enter verification code (e.g., A1B2C3D4)"
                              value={linkCode}
                              onChange={(e) => setLinkCode(e.target.value.toUpperCase())}
                              className="bg-white/5 border-white/10 font-mono uppercase"
                              maxLength={8}
                              data-testid="telegram-link-code"
                            />
                            <Button 
                              onClick={linkTelegram} 
                              disabled={linking || !linkCode.trim()}
                              className="bg-blue-600 hover:bg-blue-700"
                              data-testid="telegram-link-btn"
                            >
                              {linking ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Link2 className="w-4 h-4 mr-2" />
                                  Link Account
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Notification Settings */}
                      {telegramStatus.user_connected && (
                        <div className="space-y-4 pt-4 border-t border-white/10">
                          <h4 className="font-medium">Notification Settings</h4>
                          
                          <div className="flex items-center justify-between">
                            <div>
                              <Label>Enable Telegram Notifications</Label>
                              <p className="text-xs text-muted-foreground">Receive content for approval via Telegram</p>
                            </div>
                            <Switch
                              checked={telegramSettings.enabled}
                              onCheckedChange={(checked) => setTelegramSettings(prev => ({ ...prev, enabled: checked }))}
                              data-testid="telegram-enabled-switch"
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <Label>Notify Before Posting</Label>
                              <p className="text-xs text-muted-foreground">Send content for review before auto-posting</p>
                            </div>
                            <Switch
                              checked={telegramSettings.notify_before_post}
                              onCheckedChange={(checked) => setTelegramSettings(prev => ({ ...prev, notify_before_post: checked }))}
                              data-testid="telegram-notify-switch"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Auto-approve after (hours)</Label>
                            <p className="text-xs text-muted-foreground">Content auto-approves if you don't respond within this time</p>
                            <Input
                              type="number"
                              min={1}
                              max={168}
                              value={telegramSettings.auto_approve_after_hours}
                              onChange={(e) => setTelegramSettings(prev => ({ 
                                ...prev, 
                                auto_approve_after_hours: parseInt(e.target.value) || 24 
                              }))}
                              className="w-32 bg-white/5 border-white/10"
                              data-testid="telegram-auto-approve"
                            />
                          </div>

                          <Button onClick={saveTelegramSettings} className="w-full" data-testid="save-telegram-btn">
                            Save Telegram Settings
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-center">
                      <p className="text-sm text-yellow-400">
                        Telegram bot is not configured. Please contact support.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* What is Human-in-the-Loop */}
              <Card className="glass-card border-white/5">
                <CardHeader>
                  <CardTitle className="text-lg">What is Human-in-the-Loop?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <p>When enabled, our AI will send you content previews on Telegram <strong>before</strong> posting to social media.</p>
                  <p>You'll receive a message with:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Content preview (text, image, or video)</li>
                    <li>Scheduled posting time</li>
                    <li>Target platform</li>
                  </ul>
                  <p>Then simply tap:</p>
                  <div className="flex gap-2 mt-2">
                    <Badge className="bg-green-500/20 text-green-400">✅ Approve</Badge>
                    <Badge className="bg-yellow-500/20 text-yellow-400">✏️ Edit</Badge>
                    <Badge className="bg-red-500/20 text-red-400">❌ Reject</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Integrations */}
          <TabsContent value="integrations">
            <div className="grid gap-6">
              <Card className="glass-card border-white/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-primary" />
                    API Keys
                  </CardTitle>
                  <CardDescription>Connect your social media accounts and AI services</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                    <p className="text-sm">
                      <strong>Note:</strong> Social media posting requires API access from each platform. 
                      Some platforms (TikTok, Instagram) require business accounts and approved API access.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Volume2 className="w-4 h-4" /> ElevenLabs API Key
                      <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs ml-2">
                        Coming Soon
                      </Badge>
                    </Label>
                    <Input 
                      type="password"
                      placeholder="Enter your ElevenLabs API key"
                      value={apiKeys.elevenlabs}
                      onChange={(e) => setApiKeys({...apiKeys, elevenlabs: e.target.value})}
                      className="bg-white/5 border-white/10 opacity-50"
                      data-testid="elevenlabs-key"
                      disabled
                    />
                    <p className="text-xs text-muted-foreground">
                      🔊 AI Voice Responses coming soon! YASNO will speak back to you.{' '}
                      <a href="https://elevenlabs.io" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Powered by ElevenLabs <ExternalLink className="w-3 h-3 inline" />
                      </a>
                    </p>
                  </div>

                  <div className="border-t border-white/10 pt-6">
                    <h4 className="font-semibold mb-4">Social Media Connections</h4>
                    
                    {[
                      { name: "TikTok", key: "tiktok", color: "bg-pink-500" },
                      { name: "Instagram", key: "instagram", color: "bg-purple-500" },
                      { name: "LinkedIn", key: "linkedin", color: "bg-blue-600" },
                      { name: "Twitter/X", key: "twitter", color: "bg-sky-500" }
                    ].map((platform) => (
                      <div key={platform.key} className="flex items-center gap-4 py-4 border-b border-white/5 last:border-0">
                        <div className={`w-10 h-10 rounded-lg ${platform.color} flex items-center justify-center`}>
                          <span className="text-white font-bold text-xs uppercase">
                            {platform.name.slice(0, 2)}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{platform.name}</p>
                          <p className="text-xs text-muted-foreground">Not connected</p>
                        </div>
                        <Button variant="outline" size="sm" className="rounded-full" data-testid={`connect-${platform.key}`}>
                          <Link className="w-4 h-4 mr-2" /> Connect
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Voice & AI Settings */}
          <TabsContent value="voice">
            <div className="grid gap-6">
              <Card className="glass-card border-white/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Volume2 className="w-5 h-5 text-primary" />
                    Voice Settings
                  </CardTitle>
                  <CardDescription>Configure voice interaction preferences</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Voice Feedback</Label>
                      <p className="text-xs text-muted-foreground">AI will respond with voice using ElevenLabs TTS</p>
                    </div>
                    <Switch 
                      checked={settings.voiceFeedback}
                      onCheckedChange={(v) => setSettings({...settings, voiceFeedback: v})}
                      data-testid="voice-feedback-switch"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card border-white/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="w-5 h-5 text-primary" />
                    AI Configuration
                  </CardTitle>
                  <CardDescription>Configure AI content generation</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <h4 className="font-medium mb-2">Current AI Stack</h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <span>GPT-5.2 for content strategy & scripts</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <span>GPT Image 1.5 for image generation</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <span>OpenAI Whisper for voice transcription</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-400" />
                        <span>Sora 2 for video generation (coming soon)</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
